import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { DateTime } from 'luxon'

import {
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceParams,
    DeleteTemplateServiceVendorParams,
    UpdateEzScheduleParams,
    UpdateTemplateServiceParams,
    UpdateTemplateServiceVendorParams,
} from '../../../../../hooks/useEzSchedule/types'
import { ListVendor, TemplateService } from '../../../../../models'
import {
    DAY_WIDTH,
    FIT_COLUMN_WIDTH,
    LEFT_COLUMN_WIDTH,
    MIN_FIT_DAY_WIDTH,
} from './constants'
import { useWalkScheduleStyles } from './styles'
import { toUtcIso } from './timelineRules'
import { WalkDateMenu } from './WalkDateMenu'
import { WalkScheduleChart } from './WalkScheduleChart'
import { WalkServiceLane } from './WalkServiceLane'

interface ScopeRow {
    area_count: number
    unit_count: number
    estimated_cost?: number
}

interface Props {
    zone: string
    days: DateTime[]
    services: TemplateService[]
    vendorList: ListVendor[]
    scopeByService: Map<number, ScopeRow>
    averageByVendorId: Record<number, number | null>
    workordersByVendorDate: Map<string, number>
    transferStart: DateTime | null
    transferEnd: DateTime | null
    destReady: DateTime | null
    dtReady: DateTime | null
    minDest: DateTime | null
    minDt: DateTime | null
    dayOffKeys: Set<string>
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    onAssignUnits: (templateService: TemplateService) => void
    fitted: boolean
    onFittedChange: (fitted: boolean) => void
    onOverflowChange: (overflows: boolean) => void
}

const dayKey = (day: DateTime) => day.toFormat('yyyy-LL-dd')

export const WalkScheduleBoard = (props: Props) => {
    const {
        zone,
        days,
        services,
        updateEzSchedule,
        updateTemplateServiceVendor,
        fitted,
        onFittedChange,
        onOverflowChange,
    } = props
    const classes = useWalkScheduleStyles()
    const columnWidth = fitted ? FIT_COLUMN_WIDTH : LEFT_COLUMN_WIDTH
    const scrollerRef = useRef<HTMLDivElement>(null)
    const savedScrollLeft = useRef(0)
    const [scrollerWidth, setScrollerWidth] = useState(0)
    const [selectedDay, setSelectedDay] = useState<{
        day: DateTime
        anchor: HTMLElement
    } | null>(null)

    useLayoutEffect(() => {
        const element = scrollerRef.current
        if (!element) return
        const measure = () => setScrollerWidth(element.clientWidth)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const expandedViewport = Math.max(0, scrollerWidth - LEFT_COLUMN_WIDTH)
    const fittedViewport = Math.max(0, scrollerWidth - FIT_COLUMN_WIDTH)
    const overflows =
        expandedViewport > 0 && days.length * DAY_WIDTH > expandedViewport
    const fittedDayWidth =
        days.length > 0
            ? Math.max(
                  MIN_FIT_DAY_WIDTH,
                  Math.floor(fittedViewport / days.length),
              )
            : DAY_WIDTH
    const dayWidth = fitted && overflows ? fittedDayWidth : DAY_WIDTH

    useEffect(() => {
        onOverflowChange(overflows)
    }, [overflows, onOverflowChange])

    useLayoutEffect(() => {
        const element = scrollerRef.current
        if (!element) return
        element.scrollLeft = fitted ? 0 : savedScrollLeft.current
    }, [fitted])

    useLayoutEffect(() => {
        if (!overflows && fitted) onFittedChange(false)
    }, [overflows, fitted, onFittedChange])

    const firstDay = days[0]
    const lastDay = days[days.length - 1]
    const closeMenu = () => setSelectedDay(null)
    const selectedIso = (boundary: 'start' | 'end') => {
        if (!selectedDay) return null
        return toUtcIso(selectedDay.day, boundary)
    }

    return (
        <div className={classes.body}>
            <div
                className={classes.scroller}
                ref={scrollerRef}
                onScroll={(event) => {
                    if (!fitted) {
                        savedScrollLeft.current = event.currentTarget.scrollLeft
                    }
                }}
            >
                <div
                    className={classes.leftColumn}
                    style={{ width: columnWidth, minWidth: columnWidth }}
                >
                    <div className={classes.corner}>Services</div>
                    {services.length === 0 && (
                        <div className={classes.emptyChart}>
                            Services are chosen during setup.
                        </div>
                    )}
                    {services.map((service, index) => (
                        <WalkServiceLane
                            key={service.id}
                            templateService={service}
                            index={index}
                            serviceCount={services.length}
                            compact={fitted}
                            vendorList={props.vendorList}
                            scope={props.scopeByService.get(service.id)}
                            averageByVendorId={props.averageByVendorId}
                            addTemplateServiceVendor={
                                props.addTemplateServiceVendor
                            }
                            deleteTemplateService={props.deleteTemplateService}
                            deleteTemplateServiceVendor={
                                props.deleteTemplateServiceVendor
                            }
                            updateTemplateServiceVendor={
                                updateTemplateServiceVendor
                            }
                            updateTemplateService={props.updateTemplateService}
                            onAssignUnits={props.onAssignUnits}
                        />
                    ))}
                </div>
                <WalkScheduleChart
                    days={days}
                    dayWidth={dayWidth}
                    services={services}
                    zone={zone}
                    compact={fitted}
                    workordersByVendorDate={props.workordersByVendorDate}
                    transferStart={props.transferStart}
                    transferEnd={props.transferEnd}
                    destReady={props.destReady}
                    dtReady={props.dtReady}
                    dayOffKeys={props.dayOffKeys}
                    onDayClick={(day, anchor) =>
                        setSelectedDay({ day, anchor })
                    }
                    onVendorRange={(vendorId, startIndex, endIndex) => {
                        const start = days[startIndex]
                        const end = days[endIndex]
                        if (!start || !end) return
                        const startIso = toUtcIso(start, 'start')
                        const endIso = toUtcIso(end, 'end')
                        if (!startIso || !endIso) return
                        updateTemplateServiceVendor({
                            templateServiceVendorId: vendorId,
                            start_date: startIso,
                            end_date: endIso,
                        })
                    }}
                />
            </div>
            {firstDay && lastDay && (
                <WalkDateMenu
                    anchorEl={selectedDay?.anchor ?? null}
                    day={selectedDay?.day ?? null}
                    workStart={firstDay}
                    workEnd={lastDay}
                    transferStart={props.transferStart}
                    transferEnd={props.transferEnd}
                    destReady={props.destReady}
                    dtReady={props.dtReady}
                    minDest={props.minDest}
                    minDt={props.minDt}
                    isDayOff={
                        selectedDay
                            ? props.dayOffKeys.has(dayKey(selectedDay.day))
                            : false
                    }
                    onClose={closeMenu}
                    onSetTransferStart={() => {
                        const iso = selectedIso('start')
                        if (iso) updateEzSchedule({ transfer_start_date: iso })
                    }}
                    onSetTransferEnd={() => {
                        const iso = selectedIso('end')
                        if (iso) updateEzSchedule({ transfer_end_date: iso })
                    }}
                    onClearTransfer={() =>
                        updateEzSchedule({
                            transfer_start_date: null,
                            transfer_end_date: null,
                        })
                    }
                    onSetDest={() => {
                        const iso = selectedIso('start')
                        if (iso) updateEzSchedule({ dest_unit_ready_date: iso })
                    }}
                    onClearDest={() =>
                        updateEzSchedule({ dest_unit_ready_date: null })
                    }
                    onSetDt={() => {
                        const iso = selectedIso('start')
                        if (iso) updateEzSchedule({ dt_unit_ready_date: iso })
                    }}
                    onClearDt={() =>
                        updateEzSchedule({ dt_unit_ready_date: null })
                    }
                    onSetDayOff={() => {
                        const iso = selectedIso('start')
                        if (iso) updateEzSchedule({ add_day_off_date: iso })
                    }}
                    onClearDayOff={() => {
                        const iso = selectedIso('start')
                        if (iso) updateEzSchedule({ remove_day_off_date: iso })
                    }}
                />
            )}
        </div>
    )
}
